import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { Box } from '@mui/material';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  LexicalEditor,
} from 'lexical';
import { useCallback, useMemo } from 'react';
import { usePreviousDistinct } from 'react-use';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';

import {
  Block,
  blockTypeToBlockIcon,
  blockTypeToBlockName,
  iconProps,
} from './ToolbarPlugin.utils';

interface BlockOptionsDropdownListProps {
  editor: LexicalEditor;
  blockType: Block;
  omitBlockTypes?: Block[];
  /** When true, hides the start icon, uses auto width, transparent variant, and xs size */
  compact?: boolean;
}

export function BlockOptionsDropdown({
  editor,
  blockType,
  omitBlockTypes,
  compact,
}: BlockOptionsDropdownListProps) {
  const formatParagraph = useCallback(() => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  }, [blockType, editor]);

  const formatLargeHeading = useCallback(() => {
    if (blockType !== 'h1') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode('h1'));
        }
      });
    }
  }, [blockType, editor]);

  const formatMediumHeading = useCallback(() => {
    if (blockType !== 'h2') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode('h2'));
        }
      });
    }
  }, [blockType, editor]);

  const formatSmallHeading = useCallback(() => {
    if (blockType !== 'h3') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode('h3'));
        }
      });
    }
  }, [blockType, editor]);

  const formatBulletList = useCallback(() => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [blockType, editor]);

  const formatNumberedList = useCallback(() => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  }, [blockType, editor]);

  const formatQuote = useCallback(() => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createQuoteNode());
      });
    } else {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createQuoteNode());
      });
    }
  }, [blockType, editor]);

  const prevBlockType = usePreviousDistinct(blockType);

  const label = useMemo(() => {
    return (
      blockTypeToBlockName[blockType] ??
      (prevBlockType ? blockTypeToBlockName[prevBlockType] : 'Body')
    );
  }, [blockType, prevBlockType]);

  const IconComponent = useMemo(() => {
    if (!(blockType in blockTypeToBlockIcon)) {
      return blockTypeToBlockIcon.paragraph;
    }

    return (
      blockTypeToBlockIcon[blockType] ??
      (prevBlockType
        ? blockTypeToBlockIcon[prevBlockType]
        : blockTypeToBlockIcon.paragraph)
    );
  }, [blockType, prevBlockType]);

  const items = useMemo(() => {
    return [
      {
        type: 'h1' as const,
        label: blockTypeToBlockName.h1,
        Icon: blockTypeToBlockIcon.h1,
        clickHandler: formatLargeHeading,
      },
      {
        type: 'h2' as const,
        label: blockTypeToBlockName.h2,
        Icon: blockTypeToBlockIcon.h2,
        clickHandler: formatMediumHeading,
      },
      {
        type: 'h3' as const,
        label: blockTypeToBlockName.h3,
        Icon: blockTypeToBlockIcon.h3,
        clickHandler: formatSmallHeading,
      },
      {
        type: 'paragraph' as const,
        label: blockTypeToBlockName.paragraph,
        Icon: blockTypeToBlockIcon.paragraph,
        clickHandler: formatParagraph,
      },
      {
        type: 'ul' as const,
        label: blockTypeToBlockName.ul,
        Icon: blockTypeToBlockIcon.ul,
        clickHandler: formatBulletList,
      },
      {
        type: 'ol' as const,
        label: blockTypeToBlockName.ol,
        Icon: blockTypeToBlockIcon.ol,
        clickHandler: formatNumberedList,
      },
      {
        type: 'quote' as const,
        label: blockTypeToBlockName.quote,
        Icon: blockTypeToBlockIcon.quote,
        clickHandler: formatQuote,
      },
    ].filter((i) => {
      if (!omitBlockTypes) {
        return true;
      }

      return !omitBlockTypes.includes(i.type);
    });
  }, [
    formatBulletList,
    formatLargeHeading,
    formatMediumHeading,
    formatNumberedList,
    formatParagraph,
    formatQuote,
    formatSmallHeading,
    omitBlockTypes,
  ]);

  return (
    <ButtonWithPopover
      sx={{
        width: compact ? 'auto' : '240px',
      }}
      contentBoxSx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
      }}
      size={compact ? 'xs' : 'sm'}
      variant={compact ? 'transparent' : 'secondary'}
      popperVariant="menuBelow"
      popperProps={{
        sx: { zIndex: 999 },
        disablePortal: false,
      }}
      startIcon={compact ? undefined : IconComponent || undefined}
      buttonSx={compact ? { borderRadius: '4px', px: 0.5 } : undefined}
      label={
        <Box
          component="span"
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {label}
        </Box>
      }
    >
      {items.map(({ type, label: itemLabel, Icon, clickHandler }) => (
        <MenuItem
          key={type}
          justifyContent="space-between"
          icon={<Icon {...iconProps} />}
          label={itemLabel}
          selected={type === blockType}
          onClick={clickHandler}
        />
      ))}
    </ButtonWithPopover>
  );
}
